import React from "react";
import { Navigate } from "react-router-dom";
import Auth from "layouts/auth";

// Admin layout
import Admin from "layouts/admin";

import MainDashboard from "views/admin/default";
import Products from "views/admin/products";
import Inventory from "views/admin/inventory";
import Orders from "views/admin/orders";
import Transactions from "views/admin/transactions";
import Finance from "views/admin/finance";
import Shifts from "views/admin/shifts";
import Customers from "views/admin/customers";
import Discounts from "views/admin/discounts";
import NFTMarketplace from "views/admin/marketplace";
import StockoThemes from "@/views/admin/stocko-themes";
import Help from "views/admin/help";

// Account settings layout
import AccountSettings from "./layouts/admin/AccountSettings";

// Nested settings' pages
import BusinessAccountSettings from "./views/admin/account-settings/BusinessAccountSettings";
import StaffSettings from "./views/admin/account-settings/StaffSettings";
import WebsiteDomainSettings from "./views/admin/account-settings/WebsiteDomainSettings";
import OrderCheckoutSettings from "./views/admin/account-settings/OrderCheckoutSettings";
import SubscriptionPlansSettings from "./views/admin/account-settings/SubscriptionPlansSettings";
import PaymentsBillingsSettings from "./views/admin/account-settings/PaymentsBillingsSettings";

// Nested pages
import ProductDetails from "./views/admin/products/ProductDetails";
import OrderDetails from "./views/admin/orders/OrderDetails";
import TransactionDetails from "./views/admin/transactions/TransactionDetails";
import CustomerDetails from "./views/admin/customers/CustomerDetails";
import DiscountDetails from "./views/admin/discounts/DiscountDetails";
import ThemeDetails from "views/admin/marketplace/pages/ThemeDetails";
import BillingPage from "views/admin/marketplace/pages/BillingPage";
import PaymentPage from "views/admin/marketplace/pages/PaymentPage";

// Auth Imports
import SignIn from "views/auth/SignIn";
import AuthCallback from "views/auth/Callback";
import RequireAuth from "components/RequireAuth";
import StoreRedirect from "components/StoreRedirect";
import { STORE_ROOT } from "@hooks/useStorePath";

// inhouse icons
import { Home, Box, Grid, Settings, Tag } from "./components/icons_new";

// third-party icons (react-icons)
import { TfiReceipt } from "react-icons/tfi";
import {
  IoStorefrontOutline,
  IoPeopleOutline,
  IoFileTrayOutline,
  IoSwapHorizontalOutline,
  IoWalletOutline,
  IoTimeOutline,
  IoHelpCircleOutline,
} from "react-icons/io5";
import { layout } from "@chakra-ui/system";

const routes = [
  // Auth path
  {
    path: "/auth",
    element: <Auth />,
    children: [
      // hidden: the sidebar builds itself from route children, and an entry with
      // no path would render as a nameless link to /admin/undefined.
      { index: true, hidden: true, element: <Navigate to="/auth/sign-in" replace /> },
      {
        name: "Sign In",
        path: "sign-in",
        element: <SignIn />,
        hidden: true,
      },
    ],
  },

  {
    // Where stocko.io drops the browser after a successful login. Deliberately
    // OUTSIDE the Auth layout: that layout reserves the right half of the
    // screen for a background panel, which overlaps this page's centred
    // content. This is a full-screen interstitial, not a form.
    path: "/auth/callback",
    element: <AuthCallback />,
  },

  {
    // Admin layout, keyed by store handle: /store/zeri/orders, mirroring
    // admin.shopify.com/store/<handle>/orders. The handle is cosmetic - the
    // business comes from the signed-in user - so it is never used to look
    // anything up, only to make the URL identify the store.
    path: `/${STORE_ROOT}/:storeHandle`,
    element: (
      <RequireAuth>
        <Admin />
      </RequireAuth>
    ),
    children: [
      // home page
      {
        name: "Home",
        path: "home",
        icon: <Home className="h-6 w-6" />,
        element: <MainDashboard />,
        hidden: false,
        section: "Businesss Hub",
      },

      // products page
      {
        name: "Products",
        path: "products",
        icon: <Box className="h-6 w-6" />,
        element: <Products />,
        hidden: false,
        section: "Businesss Hub",
      },
      {
        name: "New Product",
        path: "products/new",
        element: <ProductDetails />,
        secondary: true,
        hidden: true,
      },
      {
        name: "Product Details",
        path: "products/:id",
        element: <ProductDetails />,
        secondary: true,
        hidden: true,
      },

      // inventory page
      {
        name: "Inventory",
        path: "inventory",
        icon: <IoFileTrayOutline className="h-6 w-6" />,
        element: <Inventory />,
        hidden: false,
        section: "Businesss Hub",
      },

      // orders page
      {
        name: "Orders",
        path: "orders",
        icon: <TfiReceipt className="h-6 w-6" />,
        element: <Orders />,
        hidden: false,
        section: "Businesss Hub",
      },
      {
        name: "Order Details",
        path: "orders/:id",
        element: <OrderDetails />,
        secondary: true,
        hidden: true,
      },

      // transactions page
      {
        name: "Transactions",
        path: "transactions",
        icon: <IoSwapHorizontalOutline className="h-6 w-6" />,
        element: <Transactions />,
        hidden: false,
        section: "Businesss Hub",
      },
      {
        name: "Transaction Details",
        path: "transactions/:id",
        element: <TransactionDetails />,
        secondary: true,
        hidden: true,
      },

      // finance page
      {
        name: "Finance",
        path: "finance",
        icon: <IoWalletOutline className="h-6 w-6" />,
        element: <Finance />,
        hidden: false,
        section: "Businesss Hub",
      },

      // shifts page (view only)
      {
        name: "Shifts",
        path: "shifts",
        icon: <IoTimeOutline className="h-6 w-6" />,
        element: <Shifts />,
        hidden: false,
        section: "Businesss Hub",
      },

      // customers page
      {
        name: "Customers",
        path: "customers",
        icon: <IoPeopleOutline className="h-6 w-6" />,
        element: <Customers />,
        hidden: false,
        section: "Businesss Hub",
      },
      {
        name: "Customer Details",
        path: "customers/:id",
        element: <CustomerDetails />,
        secondary: true,
        hidden: true,
      },

      // discounts page
      {
        name: "Discounts",
        path: "discounts",
        icon: <Tag className="h-6 w-6" />,
        element: <Discounts />,
        hidden: false,
        section: "Businesss Hub",
      },
      {
        name: "New Discount",
        path: "discounts/new",
        element: <DiscountDetails />,
        secondary: true,
        hidden: true,
      },
      {
        name: "Discount Details",
        path: "discounts/:id",
        element: <DiscountDetails />,
        secondary: true,
        hidden: true,
      },

      // theme store page
      {
        name: "Theme Store",
        path: "theme-store",
        icon: <IoStorefrontOutline className="h-6 w-6" />,
        element: <NFTMarketplace />,
        hidden: false,
        section: "Store Design",
      },
      {
        name: "Theme Details",
        path: "themes-store/theme/:id",
        element: <ThemeDetails />,
        hidden: true,
      },
      {
        name: "Billing",
        path: "billing/:id",
        element: <BillingPage />,
        hidden: true,
      },
      {
        name: "Payment",
        path: "payment/:id",
        element: <PaymentPage />,
        hidden: true,
      },

      // my themes page
      {
        name: "Stocko Themes",
        path: "stocko-themes",
        icon: <Grid className="h-6 w-6" />,
        element: <StockoThemes />,
        hidden: false,
        section: "Store Design",
      },

      // settings layout
      {
        name: "Account Settings",
        path: "account-settings",
        icon: <Settings className="h-6 w-6" />,
        element: <AccountSettings />,
        hidden: false,
        children: [
          { path: "business-account", element: <BusinessAccountSettings /> },
          { path: "staff-settings", element: <StaffSettings /> },
          { path: "website-domain", element: <WebsiteDomainSettings /> },
          { path: "orders-checkout", element: <OrderCheckoutSettings /> },
          { path: "subscription-plans", element: <SubscriptionPlansSettings /> },
          { path: "payments-billing", element: <PaymentsBillingsSettings /> },
          
          // index route fallback if user visits '/account-settings'
          { index: true, element: <div>Select a settings tab</div> },
        ],
        section: "Settings & Help",
      },

      // help & support page
      {
        name: "Help & Support",
        path: "help-support",
        icon: <IoHelpCircleOutline className="h-6 w-6" />,
        element: <Help />,
        hidden: false,
        section: "Settings & Help",
      },
    ],
  },

  // Home path - resolves the store handle before routing, since it is only
  // known once the business documents have loaded.
  {
    path: "/",
    element: <StoreRedirect to="home" />,
  },

  // Legacy /admin/* URLs from before the move to /store/<handle>/*. Kept so old
  // bookmarks, and the sign-in callback of any stale cached bundle, still work.
  {
    path: "/admin/*",
    element: <StoreRedirect to="home" fromLegacyAdmin />,
  },
];
export default routes;
